/**
 * Phase 3E: Clarification Prompts Panel
 * 
 * UI component for surgeons to respond to clarification prompts
 * for CPTs with INSUFFICIENT or AMBIGUOUS documentation.
 * 
 * Features:
 * - Lists open prompts grouped by CPT
 * - YES/NO/NA response options
 * - Demo mode auto-resolution
 * - Progress indicator
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  HelpCircle,
  CheckCircle2,
  XCircle,
  MinusCircle,
  Loader2,
  MessageSquareWarning,
  Sparkles,
  AlertTriangle,
  Info,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { ClarificationPrompt, ClarificationResponse } from '@/lib/phase3/types';
import { useDemoMode } from '@/contexts/DemoModeContext';

interface ClarificationPromptsPanelProps {
  prompts: ClarificationPrompt[];
  openPrompts: ClarificationPrompt[];
  resolvedPrompts: ClarificationPrompt[];
  isLoading: boolean;
  onRespond: (promptId: string, response: ClarificationResponse) => Promise<boolean>;
  onDismiss: (promptId: string, reason?: string) => Promise<boolean>;
  onAutoResolveAll?: () => Promise<boolean>;
}

interface PromptCardProps {
  prompt: ClarificationPrompt;
  onRespond: (response: ClarificationResponse) => void;
  isResponding: boolean;
}

function PromptCard({ prompt, onRespond, isResponding }: PromptCardProps) {
  const [selectedResponse, setSelectedResponse] = useState<ClarificationResponse | null>(
    prompt.response
  );
  
  const handleSubmit = () => {
    if (selectedResponse) {
      onRespond(selectedResponse);
    }
  };
  
  const isResolved = prompt.status === 'resolved' || prompt.status === 'dismissed';
  
  return (
    <div className={cn(
      "p-4 rounded-lg border",
      isResolved ? "bg-muted/30 border-muted" : "bg-background border-border"
    )}>
      <div className="flex items-start gap-3">
        <div className="shrink-0 mt-0.5">
          {isResolved ? (
            <CheckCircle2 className="w-5 h-5 text-success" />
          ) : (
            <HelpCircle className="w-5 h-5 text-warning" />
          )}
        </div>
        
        <div className="flex-1 space-y-3">
          <p className="text-sm font-medium leading-relaxed">
            {prompt.prompt_text}
          </p>
          
          {!isResolved ? (
            <>
              <RadioGroup
                value={selectedResponse || ''}
                onValueChange={(v) => setSelectedResponse(v as ClarificationResponse)}
                className="flex flex-wrap gap-4"
                disabled={isResponding}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id={`${prompt.id}-yes`} />
                  <Label 
                    htmlFor={`${prompt.id}-yes`}
                    className="flex items-center gap-1 cursor-pointer"
                  >
                    <CheckCircle2 className="w-4 h-4 text-success" />
                    Yes
                  </Label>
                </div>
                
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id={`${prompt.id}-no`} />
                  <Label 
                    htmlFor={`${prompt.id}-no`}
                    className="flex items-center gap-1 cursor-pointer"
                  >
                    <XCircle className="w-4 h-4 text-destructive" />
                    No
                  </Label>
                </div>
                
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="na" id={`${prompt.id}-na`} />
                  <Label 
                    htmlFor={`${prompt.id}-na`}
                    className="flex items-center gap-1 cursor-pointer"
                  >
                    <MinusCircle className="w-4 h-4 text-muted-foreground" />
                    N/A
                  </Label>
                </div>
              </RadioGroup>
              
              <Button
                size="sm"
                onClick={handleSubmit}
                disabled={!selectedResponse || isResponding}
              >
                {isResponding ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                )}
                Submit Response
              </Button>
            </>
          ) : (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>Response:</span>
              <Badge variant="secondary" className={cn(
                "text-xs",
                prompt.response === 'yes' && "bg-success/10 text-success",
                prompt.response === 'no' && "bg-destructive/10 text-destructive",
                prompt.response === 'na' && "bg-muted text-muted-foreground"
              )}>
                {prompt.response === 'yes' && 'Yes, performed'}
                {prompt.response === 'no' && 'No, not performed'}
                {prompt.response === 'na' && 'Not applicable'}
              </Badge>
              {prompt.responded_at && (
                <span className="text-xs text-muted-foreground">
                  • {new Date(prompt.responded_at).toLocaleDateString()}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function ClarificationPromptsPanel({
  prompts,
  openPrompts,
  resolvedPrompts,
  isLoading,
  onRespond,
  onDismiss,
  onAutoResolveAll,
}: ClarificationPromptsPanelProps) {
  const { isDemoMode } = useDemoMode();
  const [respondingPromptId, setRespondingPromptId] = useState<string | null>(null);
  const [isAutoResolving, setIsAutoResolving] = useState(false);
  
  // Group prompts by CPT code
  const promptsByCpt = prompts.reduce<Record<string, ClarificationPrompt[]>>((acc, prompt) => {
    if (!acc[prompt.cpt_code]) {
      acc[prompt.cpt_code] = [];
    }
    acc[prompt.cpt_code].push(prompt);
    return acc;
  }, {});
  
  const handleRespond = async (promptId: string, response: ClarificationResponse) => {
    setRespondingPromptId(promptId);
    await onRespond(promptId, response);
    setRespondingPromptId(null);
  };
  
  const handleAutoResolveAll = async () => {
    if (!onAutoResolveAll) return;
    setIsAutoResolving(true);
    await onAutoResolveAll();
    setIsAutoResolving(false);
  };
  
  const totalPrompts = prompts.length;
  const resolvedCount = resolvedPrompts.length;
  const progressPercent = totalPrompts > 0 ? (resolvedCount / totalPrompts) * 100 : 0;
  
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquareWarning className="w-5 h-5" />
            Clarification Prompts
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }
  
  if (prompts.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-success" />
            No Clarifications Needed
          </CardTitle>
          <CardDescription>
            All documentation requirements have been met. Proceed to surgeon confirmation.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }
  
  return (
    <Card className={cn(
      "border-2",
      openPrompts.length > 0 ? "border-warning/50" : "border-success/50"
    )}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <MessageSquareWarning className={cn(
              "w-5 h-5",
              openPrompts.length > 0 ? "text-warning" : "text-success"
            )} />
            Clarification Prompts
            {isDemoMode && (
              <Badge variant="secondary" className="ml-2 text-xs">Demo</Badge>
            )}
          </CardTitle>
          
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              {resolvedCount} / {totalPrompts} resolved
            </Badge>
          </div>
        </div>
        
        <CardDescription>
          The following documentation elements require clarification before CPT confirmation.
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Progress bar */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Clarification Progress</span>
            <span className="font-medium">{Math.round(progressPercent)}%</span>
          </div>
          <Progress value={progressPercent} className="h-2" />
        </div>
        
        {/* Demo mode auto-resolve */}
        {isDemoMode && openPrompts.length > 0 && onAutoResolveAll && (
          <Button
            variant="outline"
            onClick={handleAutoResolveAll}
            disabled={isAutoResolving}
            className="w-full"
          >
            {isAutoResolving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4 mr-2" />
            )}
            Demo: Auto-Resolve All as "Yes"
          </Button>
        )}
        
        {/* Open prompts alert */}
        {openPrompts.length > 0 && (
          <Alert variant="destructive" className="bg-warning/10 border-warning/30">
            <AlertTriangle className="w-4 h-4 text-warning" />
            <AlertDescription className="text-warning-foreground">
              {openPrompts.length} clarification{openPrompts.length > 1 ? 's' : ''} pending. 
              Surgeon confirmation is blocked until all are resolved.
            </AlertDescription>
          </Alert>
        )}
        
        <Separator />
        
        {/* Prompts grouped by CPT */}
        <Accordion type="multiple" defaultValue={Object.keys(promptsByCpt)} className="space-y-2">
          {Object.entries(promptsByCpt).map(([cptCode, cptPrompts]) => {
            const openCount = cptPrompts.filter(p => p.status === 'open').length;
            const allResolved = openCount === 0;
            
            return (
              <AccordionItem 
                key={cptCode} 
                value={cptCode}
                className={cn(
                  "border rounded-lg px-4",
                  allResolved ? "border-success/30 bg-success/5" : "border-warning/30 bg-warning/5"
                )}
              >
                <AccordionTrigger className="py-3 hover:no-underline">
                  <div className="flex items-center gap-3">
                    <span className="font-mono font-medium">CPT {cptCode}</span>
                    {allResolved ? (
                      <Badge variant="secondary" className="bg-success/10 text-success text-xs">
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        All Resolved
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="bg-warning/10 text-warning text-xs">
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        {openCount} Pending
                      </Badge>
                    )}
                  </div>
                </AccordionTrigger>
                
                <AccordionContent className="pb-4">
                  <div className="space-y-3">
                    {cptPrompts.map(prompt => (
                      <PromptCard
                        key={prompt.id}
                        prompt={prompt}
                        onRespond={(response) => handleRespond(prompt.id, response)}
                        isResponding={respondingPromptId === prompt.id}
                      />
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
        
        {/* Info notice */}
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 flex items-start gap-2">
          <Info className="w-4 h-4 text-primary shrink-0 mt-0.5" />
          <p className="text-sm text-muted-foreground">
            <strong className="text-foreground">Response Impact:</strong>{' '}
            "Yes" answers contribute to documentation sufficiency. 
            "No" answers may result in CPT being marked as not performed. 
            "N/A" indicates the element is not relevant to this case.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
